import { Children, useCallback, useEffect, useRef, useState, createContext, useContext, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth'
import {
  api,
  ApiFailure,
  ageLabel,
  genderShort,
  VISIT_TYPE_LABEL,
  type Appointment,
  type AppointmentStatus,
  type ClinicSettings,
  type Doctor,
  type Gender,
  type PatientCategory,
  type QueueRow,
  type VisitStatus,
} from '../../api'
import { getLang, tr, useT } from './i18n'
import { CategoryPill } from './category'
import '../../mobile.css'

/** A translator, as useT() hands out; helpers outside React take one. */
type Translate = typeof tr


/* ------------------------------------------------------------------ */
/* Shared furniture for the mobile app page: formatting helpers, the   */
/* icon set, the screen chrome (status bar, top nav, tab bar) and the  */
/* cards that more than one screen renders. Screens live beside this   */
/* file and import from it, so the styling stays one system.           */
/* ------------------------------------------------------------------ */
/* ---------- small helpers ---------- */

export function today(): string {
  return new Date().toLocaleDateString('en-CA')
}

/** The device-local calendar day (YYYY-MM-DD) an instant falls on. */
export function localDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA')
}

export function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1)
  dt.setDate(dt.getDate() + days)
  return dt.toLocaleDateString('en-CA')
}

export function parseDate(date: string): Date {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y!, (m ?? 1) - 1, d ?? 1)
}

/**
 * The locale weekday and month names are written in: the app's chosen
 * language, Indian style. Digits stay Latin in every language (Marathi would
 * otherwise switch to Devanagari numerals), so dates read like the rest of
 * the numbers on screen.
 */
export function dateLocale(): string {
  const lang = getLang()
  return `${lang === 'en' ? 'en' : lang}-IN-u-nu-latn`
}

/** A time as the designs print it: "09:18 AM". */
export function clock12(iso: string): string {
  const d = new Date(iso)
  const h = d.getHours()
  return `${String(((h + 11) % 12) + 1).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

export function hm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** The device's time zone, short. Times are stored UTC and rendered local. */
export function zoneLabel(): string {
  // en-IN to match money(): it names the clinic's zone "IST" where the default
  // locale spells the same offset "GMT+5:30", which is correct and unreadable.
  const parts = new Intl.DateTimeFormat('en-IN', { timeZoneName: 'short' }).formatToParts(new Date())
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
}

/** "Thu, 14 Sep at 03:30 PM IST" - the one-line date the kit's cards use. */
export function whenLabel(iso: string): string {
  const d = new Date(iso)
  const day = d.toLocaleDateString(dateLocale(), { weekday: 'short', day: 'numeric', month: 'short' })
  const zone = zoneLabel()
  return tr('shell.dayAtTime', { day, time: `${hm(iso)}${zone ? ` ${zone}` : ''}` })
}

/**
 * The two halves of {@link whenLabel}, for cards that give the day and the
 * time their own icon side by side.
 *
 * Worth separating because they answer different questions - "which day am I
 * coming in" and "what time do I need to be there" - and a single run of
 * text makes the reader parse the whole string to find either.
 */
export function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(dateLocale(), {
    weekday: 'short', day: 'numeric', month: 'short',
  })
}

export function timeLabel(iso: string): string {
  const zone = zoneLabel()
  return `${hm(iso)}${zone ? ` ${zone}` : ''}`
}

export function prettyDate(date: string): string {
  return parseDate(date).toLocaleDateString(dateLocale(), { weekday: 'short', day: 'numeric', month: 'short' })
}

/** "14 Sep" - for stat tiles where the weekday would wrap. */
export function shortDate(date: string): string {
  return parseDate(date).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short' })
}

/** The clinic's own spelling of a doctor's name. */
export function drName(name: string): string {
  return name.replace(/^dr\.?\s*/i, 'Dr ')
}

/** The name without the title, for initials and greetings. */
export function stripDr(name: string): string {
  return name.replace(/^dr\.?\s*/i, '')
}

const HONORIFIC = /^(dr|nurse|baby|master|mr|mrs|ms|miss|shri|smt|kumari)\.?\s+/i
/** Words that make an account name a desk or a role rather than a person. */
const ROLE_WORD = /\b(desk|front|reception|clinic|admin|administrator|operations|owner|counter|staff|lab|pharmacy)\b/i

/**
 * What to call someone in a greeting: "Anjali" for Dr. Anjali Rao, "Priya"
 * for Nurse Priya Shah, "Aarav" for Baby Aarav Kulkarni. A shared account
 * named for its desk keeps its whole name - "Hi, Front Desk", not "Hi, Front".
 */
export function greetingName(fullName: string | null | undefined): string {
  const name = (fullName ?? '').trim()
  if (!name) return tr('shell.greetingFallback')
  if (ROLE_WORD.test(name)) return name
  return name.replace(HONORIFIC, '').split(/\s+/)[0] || name
}

/** What a doctor's specialisation says when the roster has not been filled in.
 *  English; a translated screen renders specialtyFallback() instead. */
export const SPECIALTY_FALLBACK = 'General practice'

/** {@link SPECIALTY_FALLBACK} in the chosen language. */
export function specialtyFallback(): string {
  return tr('shell.specialtyFallback')
}

export function initials(name: string): string {
  return stripDr(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('')
}

/** Stable avatar tone per name so the same doctor always gets the same colour. */
export function tone(name: string): string {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0
  const n = h % 4
  return n === 0 ? '' : ` tone-${n + 1}`
}

export function money(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—'
  return `₹${Number(v).toLocaleString('en-IN')}`
}

/** Money on a printed receipt: money() drops the paisa, a receipt keeps it. */
export function moneyExact(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—'
  return `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** A booking's reference, short enough to read down a phone. Derived from
 *  the id, so there is no counter to keep. */
export function bookingRef(id: string): string {
  const hex = id.replace(/-/g, '').toUpperCase()
  return `${hex.slice(0, 4)}-${hex.slice(4, 8)}`
}

export function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return tr('goodMorning')
  if (h < 17) return tr('goodAfternoon')
  return tr('goodEvening')
}

export const LIVE = new Set<AppointmentStatus>(['booked', 'confirmed', 'arrived', 'in_consult'])

export function apptPillClass(s: AppointmentStatus): string {
  if (s === 'completed' || s === 'confirmed') return 'ok'
  if (s === 'cancelled' || s === 'no_show') return 'danger'
  if (s === 'rescheduled') return 'muted'
  if (s === 'in_consult') return 'navy'
  if (s === 'arrived') return 'warn'
  return ''
}

export function visitPillClass(s: VisitStatus): string {
  if (s === 'completed') return 'ok'
  if (s === 'in_consult') return 'navy'
  if (s === 'left_without_seeing') return 'danger'
  return 'warn'
}

/* ---------- icons ----------
 * Tabler Icons (https://tabler.io/icons), MIT licensed, pinned to v3.31.0 and
 * flattened to one `d` string each so <Icon> stays a single <path> and the app
 * ships no icon dependency for a set this small. Tabler draws on a 24px stroke
 * grid with round caps, which is the kit's line style exactly, and its medical
 * range is the point of using it: a stethoscope that reads as one, a real
 * syringe, lungs, a microscope, an Rx, rather than a generic glyph standing in.
 *
 * To add or change one: take the `d` attributes from the outline SVG at
 * cdn.jsdelivr.net/npm/@tabler/icons@3.31.0/icons/outline/<name>.svg, drop the
 * transparent 'M0 0h24v24H0z' bounding box Tabler opens every icon with, and
 * join what is left - each sub-path carries its own M, so they concatenate.
 */
export const PATHS = {
  /* chrome, navigation and actions */
  home: 'M5 12l-2 0l9-9l9 9l-2 0M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7M9 21v-6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v6',
  search: 'M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0-14 0M21 21l-6-6',
  bell: 'M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2-3v-3a7 7 0 0 1 4-6M9 17v1a3 3 0 0 0 6 0v-1',
  calendar: 'M4 5m0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2zM16 3l0 4M8 3l0 4M4 11l16 0M8 15h2v2h-2z',
  users: 'M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85',
  user: 'M8 7a4 4 0 1 0 8 0a4 4 0 0 0-8 0M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2',
  back: 'M15 6l-6 6l6 6',
  chevron: 'M9 6l6 6l-6 6',
  clock: 'M3 12a9 9 0 1 0 18 0a9 9 0 0 0-18 0M12 7v5l3 3',
  pin: 'M9 11a3 3 0 1 0 6 0a3 3 0 0 0-6 0M17.657 16.657l-4.243 4.243a2 2 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0z',
  check: 'M5 12l5 5l10-10',
  x: 'M18 6l-12 12M6 6l12 12',
  phone: 'M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5-2.5l5 2v4a2 2 0 0 1-2 2a16 16 0 0 1-15-15a2 2 0 0 1 2-2',
  plus: 'M12 5l0 14M5 12l14 0',
  alert: 'M12 9v4M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636-2.87l-8.106-13.536a1.914 1.914 0 0 0-3.274 0zM12 16h.01',
  logout: 'M14 8v-2a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-2M9 12h12l-3-3M18 15l3-3',
  desktop: 'M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-16a1 1 0 0 1-1-1v-10zM7 20h10M9 16v4M15 16v4',
  key: 'M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1-4.069 0l-.301-.301l-6.558 6.558a2 2 0 0 1-1.239 .578l-.175 .008h-1.172a1 1 0 0 1-.993-.883l-.007-.117v-1.172a2 2 0 0 1 .467-1.284l.119-.13l.414-.414h2v-2h2v-2l2.144-2.144l-.301-.301a2.877 2.877 0 0 1 0-4.069l2.643-2.643a2.877 2.877 0 0 1 4.069 0zM15 9h.01',
  star: 'M12 17.75l-6.172 3.245l1.179-6.873l-5-4.867l6.9-1l3.086-6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z',
  file: 'M14 3v4a1 1 0 0 0 1 1h4M17 21h-10a2 2 0 0 1-2-2v-14a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2zM9 9l1 0M9 13l6 0M9 17l6 0',
  grid: 'M4 4m0 1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1zM14 4m0 1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1zM4 14m0 1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1zM14 14m0 1a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1z',
  rupee: 'M18 5h-11h3a4 4 0 0 1 0 8h-3l6 6M7 9l11 0',
  printer: 'M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2h-14a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2M17 9v-4a2 2 0 0 0-2-2h-6a2 2 0 0 0-2 2v4M7 13m0 2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2z',
  download: 'M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2M7 11l5 5l5-5M12 4l0 12',
  trash: 'M4 7l16 0M10 11l0 6M14 11l0 6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7v-3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3',
  refresh: 'M20 11a8.1 8.1 0 0 0-15.5-2m-.5-4v4h4M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4',
  save: 'M6 4h10l4 4v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0M14 4l0 4l-6 0l0-4',
  shield: 'M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1-8.5 15a12 12 0 0 1-8.5-15a12 12 0 0 0 8.5-3',
  box: 'M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3zM12 12l8-4.5M12 12v9M12 12L4 7.5M16 5.25l-8 4.5',
  database: 'M12 6m-8 0a8 3 0 1 0 16 0a8 3 0 1 0-16 0M4 6v6a8 3 0 0 0 16 0v-6M4 12v6a8 3 0 0 0 16 0v-6',
  edit: 'M4 20h4l10.5-10.5a2.828 2.828 0 1 0-4-4l-10.5 10.5v4M13.5 6.5l4 4',
  sliders: 'M4 10a2 2 0 1 0 4 0a2 2 0 0 0-4 0M6 4v4M6 12v8M10 16a2 2 0 1 0 4 0a2 2 0 0 0-4 0M12 4v10M12 18v2M16 7a2 2 0 1 0 4 0a2 2 0 0 0-4 0M18 4v1M18 9v11',
  send: 'M10 14l11-11M21 3l-6.5 18a.55 .55 0 0 1-1 0l-3.5-7l-7-3.5a.55 .55 0 0 1 0-1l18-6.5',
  share: 'M6 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M18 6m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M8.7 10.7l6.6-3.4M8.7 13.3l6.6 3.4',
  message: 'M8 9h8M8 13h6M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-5l-5 3v-3h-2a3 3 0 0 1-3-3v-8a3 3 0 0 1 3-3h12z',
  mail: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-14a2 2 0 0 1-2-2v-10zM3 7l9 6l9-6',
  hourglass: 'M6.5 7h11M6.5 17h11M6 20v-2a6 6 0 1 1 12 0v2a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1zM6 4v2a6 6 0 1 0 12 0v-2a1 1 0 0 0-1-1h-10a1 1 0 0 0-1 1z',
  userPlus: 'M8 7a4 4 0 1 0 8 0a4 4 0 0 0-8 0M16 19h6M19 16v6M6 21v-2a4 4 0 0 1 4-4h4',
  sun: 'M12 12m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0M3 12h1m8-9v1m8 8h1m-9 8v1m-6.4-15.4l.7.7m12.1-.7l-.7.7m0 11.4l.7.7m-12.1-.7l-.7.7',
  moon: 'M12 3c.132 0 .263 0 .393 0a7.5 7.5 0 0 0 7.92 12.446a9 9 0 1 1-8.313-12.454z',
  clipboard: 'M9 5h-2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-12a2 2 0 0 0-2-2h-2M9 3m0 2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2zM9 12h6M9 16h6',

  /* clinical - the reason for taking a medical set rather than a general one */
  stethoscope: 'M6 4h-1a2 2 0 0 0-2 2v3.5h0a5.5 5.5 0 0 0 11 0v-3.5a2 2 0 0 0-2-2h-1M8 15a6 6 0 1 0 12 0v-3M11 3v2M6 3v2M20 10m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0',
  activity: 'M3 12h4l3 8l4-16l3 8h4',
  heart: 'M19.5 12.572l-7.5 7.428l-7.5-7.428a5 5 0 1 1 7.5-6.566a5 5 0 1 1 7.5 6.572',
  bone: 'M15 3a3 3 0 0 1 3 3a3 3 0 1 1-2.12 5.122l-4.758 4.758a3 3 0 1 1-5.117 2.297l0-.177l-.176 0a3 3 0 1 1 2.298-5.115l4.758-4.758a3 3 0 0 1 2.12-5.122z',
  eye: 'M10 12a2 2 0 1 0 4 0a2 2 0 0 0-4 0M21 12c-2.4 4-5.4 6-9 6c-3.6 0-6.6-2-9-6c2.4-4 5.4-6 9-6c3.6 0 6.6 2 9 6',
  tooth: 'M12 5.5c-1.074-.586-2.583-1.5-4-1.5c-2.1 0-4 1.247-4 5c0 4.899 1.056 8.41 2.671 10.537c.573 .756 1.97 .521 2.567-.236c.398-.505 .819-1.439 1.262-2.801c.292-.771 .892-1.504 1.5-1.5c.602 0 1.21 .737 1.5 1.5c.443 1.362 .864 2.295 1.262 2.8c.597 .759 2 .993 2.567 .237c1.615-2.127 2.671-5.637 2.671-10.537c0-3.74-1.908-5-4-5c-1.423 0-2.92 .911-4 1.5zM12 5.5l3 1.5',
  baby: 'M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0-18 0M9 10l.01 0M15 10l.01 0M9.5 15a3.5 3.5 0 0 0 5 0M12 3a2 2 0 0 0 0 4',
  brain: 'M15.5 13a3.5 3.5 0 0 0-3.5 3.5v1a3.5 3.5 0 0 0 7 0v-1.8M8.5 13a3.5 3.5 0 0 1 3.5 3.5v1a3.5 3.5 0 0 1-7 0v-1.8M17.5 16a3.5 3.5 0 0 0 0-7h-.5M19 9.3v-2.8a3.5 3.5 0 0 0-7 0M6.5 16a3.5 3.5 0 0 1 0-7h.5M5 9.3v-2.8a3.5 3.5 0 0 1 7 0v10',
  flask: 'M9 3l6 0M10 9l4 0M10 3v6l-4 11a.7 .7 0 0 0 .5 1h11a.7 .7 0 0 0 .5-1l-4-11v-6',
  pill: 'M4.5 12.5l8-8a4.94 4.94 0 0 1 7 7l-8 8a4.94 4.94 0 0 1-7-7M8.5 8.5l7 7',
  syringe: 'M17 3l4 4M19 5l-4.5 4.5M11.5 6.5l6 6M16.5 11.5l-6.5 6.5h-4v-4l6.5-6.5M7.5 12.5l1.5 1.5M10.5 9.5l1.5 1.5M3 21l3-3',
  thermometer: 'M10 13.5a4 4 0 1 0 4 0v-8.5a2 2 0 0 0-4 0v8.5M10 9l4 0',
  lungs: 'M6.081 20c1.612 0 2.919-1.335 2.919-2.98v-9.763c0-.694-.552-1.257-1.232-1.257c-.205 0-.405 .052-.584 .15l-.13 .083c-1.46 1.059-2.432 2.647-3.404 5.824c-.42 1.37-.636 2.962-.648 4.775c-.012 1.675 1.261 3.054 2.877 3.161l.203 .007zM17.92 20c-1.613 0-2.92-1.335-2.92-2.98v-9.763c0-.694 .552-1.257 1.233-1.257c.204 0 .405 .052 .584 .15l.13 .083c1.46 1.059 2.432 2.647 3.405 5.824c.42 1.37 .636 2.962 .648 4.775c.012 1.675-1.261 3.054-2.878 3.161l-.202 .007zM9 12a3 3 0 0 0 3-3a3 3 0 0 0 3 3M12 4v5',
  microscope: 'M5 21h14M6 18h2M7 18v3M9 11l3 3l6-6l-3-3zM10.5 12.5l-1.5 1.5M17 3l3 3M12 21a6 6 0 0 0 3.715-10.712',
  firstaid: 'M8 8v-2a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M4 8m0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2zM10 14h4M12 12v4',
  cross: 'M13 3a1 1 0 0 1 1 1v4.535l3.928-2.267a1 1 0 0 1 1.366 .366l1 1.732a1 1 0 0 1-.366 1.366l-3.927 2.268l3.927 2.269a1 1 0 0 1 .366 1.366l-1 1.732a1 1 0 0 1-1.366 .366l-3.928-2.269v4.536a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-4.536l-3.928 2.268a1 1 0 0 1-1.366-.366l-1-1.732a1 1 0 0 1 .366-1.366l3.927-2.268l-3.927-2.268a1 1 0 0 1-.366-1.366l1-1.732a1 1 0 0 1 1.366-.366l3.928 2.267v-4.535a1 1 0 0 1 1-1h2z',
  report: 'M9 5h-2a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-12a2 2 0 0 0-2-2h-2M9 3m0 2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2zM10 14l4 0M12 12l0 4',
  prescription: 'M6 19v-16h4.5a4.5 4.5 0 1 1 0 9h-4.5M19 21l-9-9M13 21l6-6',
  ear: 'M6 10a7 7 0 1 1 13 3.6a10 10 0 0 1-2 2a8 8 0 0 0-2 3a4.5 4.5 0 0 1-6.8 1.4M10 10a3 3 0 1 1 5 2.2',
  vitals: 'M3 4m0 1a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-16a1 1 0 0 1-1-1zM7 20h10M9 16v4M15 16v4M7 10h2l2 3l2-6l1 3h3',
  nurse: 'M12 5c2.941 0 6.685 1.537 9 3l-2 11h-14l-2-11c2.394-1.513 6.168-3.005 9-3M10 12h4M12 10v4',
  wheelchair: 'M8 16m-5 0a5 5 0 1 0 10 0a5 5 0 1 0-10 0M19 19m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0M19 17a3 3 0 0 0-3-3h-3.4M3 3h1a2 2 0 0 1 2 2v6M6 8h11M15 8v6',
  virus: 'M12 12m-5 0a5 5 0 1 0 10 0a5 5 0 1 0-10 0M12 7v-4M11 3h2M15.536 8.464l2.828-2.828M17.657 4.929l1.414 1.414M17 12h4M21 11v2M15.535 15.536l2.829 2.828M19.071 17.657l-1.414 1.414M12 17v4M13 21h-2M8.465 15.536l-2.829 2.828M6.343 19.071l-1.413-1.414M7 12h-4M3 13v-2M8.464 8.464l-2.828-2.828M4.929 6.343l1.414-1.413',
  droplet: 'M7.502 19.423c2.602 2.105 6.395 2.105 8.996 0c2.602-2.105 3.262-5.708 1.566-8.546l-4.89-7.26c-.42-.625-1.287-.803-1.936-.397a1.376 1.376 0 0 0-.41 .397l-4.893 7.26c-1.695 2.838-1.035 6.441 1.567 8.546z',
  bed: 'M7 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0-4 0M22 17v-3h-20M2 8v9M12 14h10v-2a3 3 0 0 0-3-3h-7v5z',
  dna: 'M14.828 14.828a4 4 0 1 0-5.656-5.656a4 4 0 0 0 5.656 5.656zM9.172 20.485a4 4 0 1 0-5.657-5.657M14.828 3.515a4 4 0 0 0 5.657 5.657',
  scan: 'M4 8v-2a2 2 0 0 1 2-2h2M4 16v2a2 2 0 0 0 2 2h2M16 4h2a2 2 0 0 1 2 2v2M16 20h2a2 2 0 0 0 2-2v-2M12 8m-1 0a1 1 0 1 0 2 0a1 1 0 1 0-2 0M10 17v-1a2 2 0 1 1 4 0v1M8 10c.666 .666 1.334 1 2 1h4c.666 0 1.334-.334 2-1M12 11v3',
  testtube: 'M20 8.04l-12.122 12.124a2.857 2.857 0 1 1-4.041-4.04l12.122-12.124M7 13h8M19 15l1.5 1.6a2 2 0 1 1-3 0l1.5-1.6zM15 3l6 6',
  whatsapp: 'M3 21l1.65-3.8a9 9 0 1 1 3.4 2.9l-5.05.9M9 10a.5.5 0 0 0 1 0v-1a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1',
} as const

export type IconName = keyof typeof PATHS

export function Icon({ name, size = 22 }: { name: IconName; size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  )
}

/* ---------- illustrations ---------- */

/*
 * The kit puts a piece of line art wherever a screen would otherwise be one
 * sentence on an empty ground - the confirmation, an onboarding pane, an empty
 * list. They are drawn here as inline SVG rather than shipped as image files
 * for three reasons: the line work takes its colour from the theme and so
 * follows it into dark mode, a clinic serving this off its own LAN never waits
 * on an image request, and there is no binary in the repository to keep in sync
 * with the palette. The accent props - capsules, the heart, the sparkles - are
 * fixed colours, because being coloured is the whole point of them.
 */

const ART_ACCENT = {
  purple: '#7C5CFF',
  pink: '#FF9BB0',
  green: '#7BC96F',
  gold: '#F3D027',
  blush: '#FBD5D5',
} as const

export type ArtName = 'booked' | 'empty' | 'doctors' | 'records'

/** A capsule tilted at `rot` degrees, half tinted - the kit's floating pills. */
function Capsule({ x, y, rot, tint }: { x: number; y: number; rot: number; tint: string }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <rect x="-13" y="-6.5" width="26" height="13" rx="6.5" fill="#fff" className="ink" strokeWidth="2" />
      <path d="M0-6.5h6.5a6.5 6.5 0 0 1 0 13H0z" fill={tint} />
      <path d="M0-6.5v13" className="ink" strokeWidth="2" />
    </g>
  )
}

/** Four-point sparkle, the kit's filler mark around an illustration. */
function Sparkle({ x, y, r, fill }: { x: number; y: number; r: number; fill?: string }) {
  return (
    <path
      d={`M${x} ${y - r}c.4 ${r * 0.6} .4 ${r * 0.6} ${r} ${r}c-.6 .4-.6 .4-${r} ${r}c-.4-${r * 0.6}-.4-${r * 0.6}-${r}-${r}c.6-.4 .6-.4 ${r}-${r}z`}
      fill={fill ?? 'none'}
      className={fill ? undefined : 'ink'}
      strokeWidth="1.6"
    />
  )
}

export function Art({ name, small }: { name: ArtName; small?: boolean }) {
  const common = {
    className: `m-art${small ? ' sm' : ''}`,
    viewBox: '0 0 240 190',
    fill: 'none' as const,
    stroke: 'none' as const,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }

  if (name === 'booked') {
    return (
      <svg {...common}>
        <g stroke="none">
          <Sparkle x={44} y={34} r={13} />
          <Sparkle x={196} y={44} r={10} />
          <Sparkle x={72} y={72} r={7} />
          <g transform="translate(160 40) rotate(-8) scale(1.35)">
            <path
              d="M0 8C-9 1-11-4-7.5-7.5-4.5-10.5-1-8.5 0-6c1-2.5 4.5-4.5 7.5-1.5C11-4 9 1 0 8z"
              fill={ART_ACCENT.purple}
            />
          </g>
        </g>
        <g className="ink" strokeWidth="2.4">
          {/* the kit's first-aid case, opened */}
          <rect x="74" y="92" width="94" height="66" rx="14" fill={ART_ACCENT.blush} />
          <path d="M74 118h94" />
          <rect x="106" y="80" width="30" height="14" rx="5" fill="#fff" />
          <path d="M121 110v30M106 125h30" strokeWidth="9" stroke="#fff" strokeLinecap="butt" />
        </g>
        <g stroke="none">
          <Capsule x={58} y={112} rot={-28} tint={ART_ACCENT.pink} />
          <Capsule x={190} y={96} rot={34} tint={ART_ACCENT.green} />
          {/* A tilted tablet, kept low and left so the props sit around the case
              rather than stacking up under the heart. */}
          <g transform="translate(46 152) rotate(-16)">
            <rect x="-22" y="-6" width="44" height="12" rx="6" fill={ART_ACCENT.gold} />
          </g>
        </g>
      </svg>
    )
  }

  if (name === 'doctors') {
    return (
      <svg {...common}>
        <g className="ink" strokeWidth="2.4">
          <rect x="62" y="56" width="116" height="104" rx="18" fill={ART_ACCENT.green} fillOpacity=".5" />
          <circle cx="120" cy="92" r="19" fill="#fff" />
          <path d="M88 150a32 32 0 0 1 64 0" fill="#fff" />
          <path d="M104 120v9a16 16 0 0 0 32 0v-9" />
        </g>
        <g stroke="none">
          <Sparkle x={48} y={48} r={11} />
          <Sparkle x={196} y={122} r={9} />
          <g transform="translate(192 56) rotate(24)">
            <rect x="-20" y="-6" width="40" height="12" rx="6" fill={ART_ACCENT.gold} />
          </g>
          <Capsule x={54} y={128} rot={-34} tint={ART_ACCENT.pink} />
        </g>
      </svg>
    )
  }

  if (name === 'records') {
    return (
      <svg {...common}>
        <g className="ink" strokeWidth="2.4">
          <rect x="70" y="48" width="100" height="116" rx="14" fill="#fff" />
          <rect x="98" y="38" width="44" height="18" rx="7" fill={ART_ACCENT.gold} />
          <path d="M90 88h60M90 108h60M90 128h36" />
        </g>
        <g stroke="none">
          <Sparkle x={50} y={62} r={10} />
          <Sparkle x={194} y={132} r={11} />
          <Capsule x={190} y={70} rot={28} tint={ART_ACCENT.green} />
        </g>
      </svg>
    )
  }

  // 'empty' - the quietest of the set, for a list with nothing in it.
  return (
    <svg {...common}>
      <g className="ink" strokeWidth="2.6">
        <circle cx="120" cy="98" r="52" fill="#fff" />
        <path d="M100 96h40M100 116h26" />
        <circle cx="103" cy="76" r="3.2" fill="currentColor" stroke="none" />
        <circle cx="137" cy="76" r="3.2" fill="currentColor" stroke="none" />
      </g>
      <g stroke="none">
        <Sparkle x={54} y={54} r={11} />
        <Sparkle x={190} y={132} r={9} />
      </g>
    </svg>
  )
}

/** The kit's loading state: a six-point sparkle turning in place. */
export function Spinner({ size = 46 }: { size?: number }) {
  return (
    <svg
      className="m-spin" width={size} height={size} viewBox="0 0 24 24"
      stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" fill="none" aria-hidden="true"
    >
      <path d="M12 3v5M12 16v5M3 12h5M16 12h5M6 6l3.5 3.5M14.5 14.5 18 18M18 6l-3.5 3.5M9.5 14.5 6 18" />
    </svg>
  )
}

/** Icon for a specialty chip, matched on keywords so unseeded names still get one. */
export function specialtyIcon(spec: string): IconName {
  const s = spec.toLowerCase()
  if (/cardio|heart/.test(s)) return 'heart'
  if (/paed|pedi|child/.test(s)) return 'baby'
  if (/ortho|bone|joint|fract/.test(s)) return 'bone'
  if (/pulmo|chest|respir|lung|tb|asthma/.test(s)) return 'lungs'
  if (/ent|ear|nose|throat|audio|oto/.test(s)) return 'ear'
  if (/derm|skin/.test(s)) return 'virus'
  if (/eye|ophth|optha|optom|vision/.test(s)) return 'eye'
  if (/dent|tooth|oral/.test(s)) return 'tooth'
  if (/neuro|psych|mental/.test(s)) return 'brain'
  if (/radio|imaging|x-?ray|scan|sono/.test(s)) return 'scan'
  if (/path|lab|haema|hema|biochem/.test(s)) return 'microscope'
  if (/physio|rehab|ortho.?ther/.test(s)) return 'wheelchair'
  if (/immun|vaccin|shot/.test(s)) return 'syringe'
  if (/gyn|obst|matern|antenat/.test(s)) return 'nurse'
  if (/surg|operat|theatre|theater/.test(s)) return 'cross'
  return 'stethoscope'
}

/* ---------- shared context ---------- */

export interface Toast { kind: 'ok' | 'warn' | 'error' | 'info'; text: string }

export interface MobileState {
  doctors: Doctor[]
  settings: ClinicSettings | null
  queue: QueueRow[]
  reloadQueue: () => Promise<void>
  /** Re-reads the clinic settings, after the administrator saves them. */
  reloadSettings?: () => void
  toast: (t: Toast) => void
}

export const MobileCtx = createContext<MobileState | null>(null)

export function useMobile(): MobileState {
  const ctx = useContext(MobileCtx)
  if (!ctx) throw new Error('useMobile outside Mobile')
  return ctx
}

export function errText(err: unknown, fallback: string): string {
  return err instanceof ApiFailure ? err.message : fallback
}
/* ---------- chrome ---------- */

export function StatusBar() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="m-statusbar" aria-hidden="true">
      <span>{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
      <span className="sig">
        <i style={{ height: 4 }} /><i style={{ height: 6 }} /><i style={{ height: 8 }} /><i style={{ height: 10 }} />
        <span className="batt" style={{ marginLeft: 6 }} />
      </span>
    </div>
  )
}

/** Who is signed in, in the terms the screens ask in. */
export function useRoleFlags() {
  const { user } = useAuth()
  const role = user?.role
  /* The modules the server says this person holds (their role's defaults plus
     what the admin changed). The server enforces them; these only decide what
     to show. An older server without them falls back to the role. */
  const access = (user as { access?: Partial<Record<string, boolean>> } | null)?.access
  const has = (m: string, fallback: boolean) => (access ? !!access[m] : fallback)
  const canManageStock = has('stock.manage', role === 'admin' || role === 'lab_manager' || !!user?.canManageStock)
  return {
    role,
    isLabManager: role === 'lab_manager',
    /** May add stock, count it and record use. */
    canManageStock,
    /** May read stock and its reports. */
    canViewStock: has('stock.view', role === 'doctor' || canManageStock),
    /** Bills, desk payments, day end. */
    canBill: has('billing', role === 'admin' || role === 'receptionist' || role === 'doctor'),
    /** The clinic's reports. */
    canReports: has('reports', role === 'admin' || role === 'doctor'),
    /** The stock ledger: purchases against tests. */
    canLedger: has('stock.ledger', role === 'admin'),
    /** Sets what each lab test uses. */
    canEditRecipes: has('lab.recipes', role === 'admin' || role === 'lab_manager' || canManageStock),
    /** Marks lab tests done and records the items used. */
    canCompleteLab: has('lab.complete', role === 'admin' || role === 'lab_manager' || role === 'doctor'),
    isAdmin: role === 'admin',
    isDoctor: role === 'doctor',
    isPatient: role === 'patient',
    isReceptionist: role === 'receptionist',
    /** Writes to the record: the pad, investigations, anything clinical. */
    isClinical: role === 'admin' || role === 'doctor',
    /** Takes bookings at the desk. */
    canBook: role === 'admin' || role === 'receptionist',
  }
}

export function Screen({ children, cta, tabs = true }: { children: ReactNode; cta?: ReactNode; tabs?: boolean }) {
  return (
    <>
      <div className={`mapp-screen${cta ? ' has-cta' : ''}${tabs ? '' : ' no-tabs'}`}>
        <StatusBar />
        {children}
        {cta && <div className="m-cta">{cta}</div>}
      </div>
      {tabs && <TabBar />}
    </>
  )
}

export function TopNav({
  title, right, onBack, backLabel, center,
}: {
  title: string
  /** Title centred between the back button and the right slot. */
  center?: boolean
  right?: ReactNode
  /** Given, this replaces going back in history - some screens back out of a mode. */
  onBack?: () => void
  backLabel?: string
}) {
  const navigate = useNavigate()
  const { t } = useT()
  return (
    <div className={`m-topnav${center ? ' center' : ''}`}>
      <button type="button" className="m-icon-btn" aria-label={backLabel ?? t('common.back')} onClick={() => (onBack ? onBack() : navigate(-1))}>
        <Icon name="back" size={20} />
      </button>
      <h1>{title}</h1>
      {right ?? <div className="m-spacer" />}
    </div>
  )
}

/** `heading` matters: on bookings the date is the page title, on day end it
 *  sits under one, and a screen should not have two h1s. */
export function DayStepper({
  date, onChange, label, heading = false,
}: {
  date: string
  onChange: (next: string) => void
  /** How the date reads. Defaults to Today / the pretty date. */
  label?: string
  heading?: boolean
}) {
  const { t } = useT()
  const text = label ?? (date === today() ? t('common.today') : prettyDate(date))
  return (
    // `center` because this nav has an arrow on either side rather than a back
    // button: the kit's left-aligned title is for the latter, and left-aligning
    // this one would hang it off the previous-day arrow.
    <div className="m-topnav center">
      <button type="button" className="m-icon-btn" aria-label={t('shell.prevDay')} onClick={() => onChange(shiftDate(date, -1))}>
        <Icon name="back" size={20} />
      </button>
      {heading ? <h1>{text}</h1> : <div className="m-stepper-title" role="status">{text}</div>}
      <button type="button" className="m-icon-btn" aria-label={t('shell.nextDay')} onClick={() => onChange(shiftDate(date, 1))}>
        <Icon name="chevron" size={20} />
      </button>
    </div>
  )
}

export function TabBar() {
  const { pathname } = useLocation()
  const { queue } = useMobile()
  const { user } = useAuth()
  const { isPatient, isLabManager, isReceptionist, isAdmin, canReports, canBill } = useRoleFlags()
  const isDesk = isReceptionist || isAdmin
  const apptsOn = useMobile().settings?.appointmentsEnabled !== false
  const { t } = useT()
  const waiting = queue.filter((v) => v.status === 'waiting').length

  /*
   * The bar is role-aware. It used to be identical for everyone, which gave a
   * patient a "Patients" tab that could only ever land on "Not available" -
   * one of five primary destinations guaranteed to be a dead end. A patient
   * gets their own record in its place, which is the thing that tab was
   * standing in for anyway.
   */
  const baseTabs: { to: string; icon: IconName; label: string; on: boolean; dot?: boolean }[] = [
    { to: '/m', icon: 'home', label: t('shell.tabHome'), on: pathname === '/m' || pathname === '/m/' },
    { to: '/m/doctors', icon: 'stethoscope', label: t('common.doctors'), on: /^\/m\/(doctors|book)/.test(pathname) },
    {
      to: '/m/appointments',
      icon: 'calendar',
      label: isPatient ? t('shell.tabMyVisits') : t('shell.tabBookings'),
      on: pathname.startsWith('/m/appointments'),
    },
    isPatient
      ? user?.patientId
        ? {
          to: `/m/patients/${user.patientId}`,
          icon: 'users',
          label: t('shell.tabMyRecord'),
          on: pathname.startsWith('/m/patients'),
        }
        : null
      : {
        to: '/m/patients',
        icon: 'users',
        label: t('common.patients'),
        on: pathname.startsWith('/m/patients'),
        dot: waiting > 0,
      },
    { to: '/m/profile', icon: 'user', label: t('shell.tabProfile'), on: pathname.startsWith('/m/profile') },
  ].filter(Boolean) as { to: string; icon: IconName; label: string; on: boolean; dot?: boolean }[]

  // The lab manager works the lab sheet and the stock room, not the consulting
  // desk, so the bar offers those instead of doctors and patients.
  const labTabs: typeof baseTabs = [
    { to: '/m', icon: 'home', label: t('shell.tabHome'), on: pathname === '/m' || pathname === '/m/' },
    { to: '/m/appointments', icon: 'testtube', label: t('stock.labTests'), on: pathname.startsWith('/m/appointments') || pathname.startsWith('/m/lab') },
    { to: '/m/stock', icon: 'box', label: t('stock.title'), on: pathname.startsWith('/m/stock') },
    { to: '/m/profile', icon: 'user', label: t('shell.tabProfile'), on: pathname.startsWith('/m/profile') },
  ]
  // The front desk books doctors from Home, so its bar trades Doctors for the lab.
  // Billing first: the desk's home is a new bill, then its bills; bookings
  // only where the clinic runs appointments.
  const deskTabs: typeof baseTabs = [
    { to: '/m', icon: 'home' as IconName, label: t('shell.tabHome'), on: pathname === '/m' || pathname === '/m/' },
    ...(canBill ? [{ to: '/m/billing', icon: 'rupee' as IconName, label: t('qb.tabBills'), on: /^\/m\/(billing|bill\/|day-end)/.test(pathname) }] : []),
    { to: '/m/patients', icon: 'users', label: t('common.patients'), on: pathname.startsWith('/m/patients'), dot: waiting > 0 },
    apptsOn
      ? { to: '/m/appointments', icon: 'calendar' as IconName, label: t('shell.tabBookings'), on: pathname.startsWith('/m/appointments') || /^\/m\/(doctors|book)/.test(pathname) }
      : { to: '/m/lab', icon: 'microscope' as IconName, label: t('home.rTabLab'), on: pathname.startsWith('/m/lab') },
    { to: '/m/profile', icon: 'user', label: t('shell.tabProfile'), on: pathname.startsWith('/m/profile') },
  ]
  // A doctor's day is the queue and the consulting room, not the roster.
  const doctorTabs: typeof baseTabs = [
    { to: '/m', icon: 'home', label: t('shell.tabHome'), on: pathname === '/m' || pathname === '/m/' },
    { to: '/m/console', icon: 'users', label: t('home.dTabQueue'), on: /^\/m\/(console|visits|patients)/.test(pathname), dot: waiting > 0 },
    { to: '/m/lab', icon: 'microscope', label: t('home.rTabLab'), on: pathname.startsWith('/m/lab') || pathname.startsWith('/m/stock') },
    ...(canReports ? [{ to: '/m/reports', icon: 'report' as IconName, label: t('home.dTabReports'), on: pathname.startsWith('/m/reports') }] : []),
    { to: '/m/profile', icon: 'user', label: t('shell.tabProfile'), on: pathname.startsWith('/m/profile') },
  ]
  const tabs = isLabManager ? labTabs : user?.doctorId ? doctorTabs : isDesk ? deskTabs : baseTabs

  return (
    <nav className="m-tabbar" aria-label={t('shell.sections')}>
      {tabs.map((tab) => (
        <Link key={tab.to} to={tab.to} className={`m-tab${tab.on ? ' is-on' : ''}`} aria-current={tab.on ? 'page' : undefined}>
          <span className="ico"><Icon name={tab.icon} size={20} />{tab.dot && !tab.on && <span className="dot" />}</span>
          {tab.label}
        </Link>
      ))}
    </nav>
  )
}

/* ---------- the two states every list screen has ---------- */

export function Loading({ children }: { children?: ReactNode }) {
  const { t } = useT()
  return <div className="m-loading" role="status">{children ?? t('common.loading')}</div>
}

export function EmptyState({
  title, children, card = true, art = true,
}: {
  title?: string
  children?: ReactNode
  /** Standalone states carry their own card; ones already inside a card do not. */
  card?: boolean
  /**
   * The kit puts a mark above the sentence so an empty list reads as a state
   * rather than as a failure. It lives here rather than at every call site, so
   * a screen gets it by using this component. A state squeezed inside another
   * card is usually one line, and passes art={false}.
   */
  art?: boolean
}) {
  return (
    <div className={card ? 'm-card m-empty' : 'm-empty'}>
      {art && card && <Art name="empty" small />}
      {title && <strong>{title}</strong>}
      {children}
    </div>
  )
}

export function Avatar({ name, size, src }: { name: string; size?: 'sm' | 'lg'; src?: string | null }) {
  return (
    <div className={`m-avatar${size ? ` ${size}` : ''}${tone(name)}`}>
      {src ? <img src={src} alt="" /> : initials(name)}
    </div>
  )
}

/* ---------- appointment loading shared by Home and Bookings ---------- */

/** The API lists one doctor per call, so a clinic-wide view merges across the roster. */
export async function loadAppointments(doctorIds: string[], date: string): Promise<Appointment[]> {
  // More than one doctor: one request for the whole clinic's day, filtered
  // here, rather than a request per doctor - eleven round trips on a phone.
  if (doctorIds.length > 1) {
    try {
      const wanted = new Set(doctorIds)
      const r = await api.get<{ appointments: Appointment[] }>(`/api/appointments?date=${date}`)
      return r.appointments.filter((a) => wanted.has(a.doctorId))
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
    } catch {
      /* an older server without the whole-day read: fall back to per doctor */
    }
  }
  const rows = await Promise.all(
    doctorIds.map((id) =>
      api.get<{ appointments: Appointment[] }>(`/api/appointments?doctorId=${id}&date=${date}`)
        .then((r) => r.appointments)
        .catch(() => [] as Appointment[]),
    ),
  )
  return rows.flat().sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
}

/**
 * A horizontal strip that advances by itself, one card at a time, and loops.
 *
 * It stops the moment someone reaches for it - a touch, a hover, keyboard
 * focus - and picks up again a few seconds after they let go, so it never
 * slides a card out from under a finger. With reduced motion asked for, or
 * when everything already fits, it does not move at all. The dots are real
 * buttons, so the strip can be stepped through without swiping.
 */
export function AutoSlider({ children, intervalMs = 3500, label, full, head }: {
  children: ReactNode
  intervalMs?: number
  /** Names the strip for screen readers, e.g. "Doctors". */
  label: string
  /** One slide per view, each the strip's full width - a banner, not a rail. */
  full?: boolean
  /**
   * Rendered above the strip, handed previous/next steppers - for a section
   * heading that carries its own arrows beside the title.
   */
  head?: (nav: { prev: () => void; next: () => void }) => ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useT()
  const count = Children.count(children)
  const [index, setIndex] = useState(0)
  const indexRef = useRef(0)
  const paused = useRef(false)
  const resume = useRef<ReturnType<typeof setTimeout> | null>(null)

  function go(i: number, smooth = true) {
    const el = ref.current
    const target = el?.children[i] as HTMLElement | undefined
    const first = el?.children[0] as HTMLElement | undefined
    if (!el || !target || !first) return
    el.scrollTo({ left: target.offsetLeft - first.offsetLeft, behavior: smooth ? 'smooth' : 'auto' })
  }

  // Keep the dot in step with wherever the strip actually is, swiped or slid.
  function onScroll() {
    const el = ref.current
    if (!el) return
    const kids = Array.from(el.children) as HTMLElement[]
    const base = kids[0]?.offsetLeft ?? 0
    let best = 0
    kids.forEach((k, i) => {
      if (Math.abs(k.offsetLeft - base - el.scrollLeft) < Math.abs((kids[best]?.offsetLeft ?? 0) - base - el.scrollLeft)) best = i
    })
    // At the far end the last card cannot scroll to the start; count it as reached.
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) best = kids.length - 1
    indexRef.current = best
    setIndex(best)
  }

  function hold() {
    paused.current = true
    if (resume.current) clearTimeout(resume.current)
  }
  /** Step by one, looping at either end; counts as the user reaching in. */
  function step(by: 1 | -1) {
    if (count < 2) return
    hold()
    const el = ref.current
    const atEnd = !!el && el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
    const target = by === 1
      ? (atEnd ? 0 : indexRef.current + 1)
      : (indexRef.current === 0 ? count - 1 : indexRef.current - 1)
    go(target)
    release()
  }
  function release(afterMs = 5000) {
    if (resume.current) clearTimeout(resume.current)
    resume.current = setTimeout(() => { paused.current = false }, afterMs)
  }

  useEffect(() => {
    if (count < 2) return
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduced) return
    const timer = setInterval(() => {
      const el = ref.current
      if (!el || paused.current || document.visibilityState !== 'visible') return
      if (el.scrollWidth <= el.clientWidth + 4) return // everything already fits
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4
      go(atEnd ? 0 : Math.min(indexRef.current + 1, count - 1))
    }, intervalMs)
    return () => { clearInterval(timer); if (resume.current) clearTimeout(resume.current) }
  }, [count, intervalMs])

  return (
    <div className={`m-slider${full ? ' full' : ''}`} role="region" aria-roledescription="carousel" aria-label={label}>
      {head?.({ prev: () => step(-1), next: () => step(1) })}
      <div
        ref={ref}
        className="m-hscroll"
        onScroll={onScroll}
        onPointerDown={hold}
        onPointerUp={() => release()}
        onPointerCancel={() => release()}
        onMouseEnter={hold}
        onMouseLeave={() => release(1500)}
        onFocus={hold}
        onBlur={() => release()}
      >
        {children}
      </div>
      {count > 1 && (
        <div className="m-slider-dots">
          {Array.from({ length: count }, (_, i) => (
            <button
              key={i} type="button"
              className={i === index ? 'is-on' : ''}
              aria-label={t('shell.slideOf', { i: i + 1, count })}
              aria-current={i === index}
              onClick={() => { hold(); go(i); release() }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** Whether an ISO instant falls on the device's today. */
function isTodayIso(iso: string): boolean {
  return new Date(iso).toLocaleDateString('en-CA') === today()
}

/**
 * A booking still marked booked or confirmed after its day has ended: nobody
 * checked the patient in and nobody closed it. It is not upcoming - it is a
 * missed appointment the desk still has to mark as a no-show, cancel or move.
 * The server refuses to confirm, check in or charge it for the same reason.
 */
export function isMissed(a: Pick<Appointment, 'status' | 'scheduledAt'>): boolean {
  return (a.status === 'booked' || a.status === 'confirmed')
    && new Date(a.scheduledAt).toLocaleDateString('en-CA') < today()
}

/** Still ahead of the patient: live, and not left behind by its own day. */
export function isUpcoming(a: Pick<Appointment, 'status' | 'scheduledAt'>): boolean {
  return LIVE.has(a.status) && !isMissed(a)
}

/**
 * Where a live booking stands in its doctor's queue, in one line: "Serial #7 ·
 * now serving #4" on the day, "Serial #7 of 12" before it. Null when there is
 * nothing useful to say - a cancelled booking has no serial.
 */
export function queueLine(appt: Appointment, t: Translate = tr): string | null {
  if (!appt.serialNo || isMissed(appt)) return null
  const q = appt.queue
  const n = appt.serialNo
  if (!q || !LIVE.has(appt.status) || !isTodayIso(appt.scheduledAt)) {
    return q ? t('shell.serialOfTotal', { n, total: q.total }) : t('shell.serialN', { n })
  }
  if (appt.status === 'in_consult') return t('shell.serialWithDoctor', { n })
  // "Serial #1 · next up #1" made the reader work out that it meant them.
  if (q.next === appt.serialNo) {
    return q.nowServing != null
      ? t('shell.serialNextAfter', { n, now: q.nowServing })
      : t('shell.serialNext', { n })
  }
  if (q.nowServing != null) return t('shell.serialNowServing', { n, now: q.nowServing })
  if (q.next != null) return t('shell.serialNextUp', { n, next: q.next })
  return t('shell.serialN', { n })
}

/** The booking's serial, as the desk calls it out. */
export function SerialBadge({ appt }: { appt: Appointment }) {
  const { t } = useT()
  if (!appt.serialNo) return null
  const active = appt.status === 'in_consult'
  const done = appt.status === 'completed'
  return (
    <span
      className={`m-serial${active ? ' is-active' : ''}${done ? ' is-done' : ''}`}
      aria-label={t(active ? 'shell.serialAriaActive' : done ? 'shell.serialAriaDone' : 'shell.serialAria', { n: appt.serialNo })}
    >
      {done ? <Icon name="check" size={14} /> : null}#{appt.serialNo}
    </span>
  )
}

export function AppointmentCard({
  appt, doctor, onAction, onPay, busy, dim, to, footer, headAction, desk,
}: {
  appt: Appointment
  doctor?: Doctor
  onAction?: (kind: 'confirm' | 'checkin' | 'cancel' | 'no-show' | 'reschedule') => void
  /** Given, the payment row gains a Pay button. Omitted when nothing is owed. */
  onPay?: () => void
  busy?: boolean
  dim?: boolean
  /** Where the card opens. Given, the status pill becomes the link to the booking. */
  to?: string
  /** Anything the screen adds under the card, e.g. a patient's own actions. */
  footer?: ReactNode
  /** A small control beside the status pill, e.g. the WhatsApp share. */
  headAction?: ReactNode
  /**
   * The desk's day sheet: serial first and the patient as the title, since
   * that is who is being called. The doctor's portrait and the clinic's
   * address, repeated on every card of one doctor's day, only made each
   * card taller.
   */
  desk?: boolean
}) {
  const { settings } = useMobile()
  const { isPatient } = useRoleFlags()
  const { t } = useT()
  const staffView = !isPatient
  const missed = isMissed(appt)
  const live = LIVE.has(appt.status) && !missed
  const active = appt.status === 'in_consult'
  const line = desk ? null : queueLine(appt, t)
  const pillClass = missed ? 'warn' : apptPillClass(appt.status)
  const pillText = missed ? t('shell.missed') : active && desk ? t('shell.nowServing') : t(`apptStatus.${appt.status}`)
  const pill = <span className={`m-pill ${pillClass}`}>{pillText}</span>
  const headEnd = (
    <div className="m-appt-head-end">
      {to ? <Link className="m-card-link" to={to} aria-label={t('shell.bookingDetails')}>{pill}</Link> : pill}
      {headAction}
    </div>
  )
  return (
    <div className={`m-card m-appt${dim ? ' is-dim' : ''}${desk ? ' is-desk' : ''}${active ? ' is-active' : ''}`}>
      {/* Who the card is about depends on who is reading it. A patient's
          own bookings lead with the doctor they are seeing; the desk and the
          doctors are working through patients, so for staff the patient
          leads and the doctor becomes a detail line. The desk's day sheet
          goes further and puts the serial first. */}
      <div className="m-appt-head">
        {desk ? (
          <>
            <SerialBadge appt={appt} />
            <div className="m-doc-body">
              <div className="m-doc-name">
                <Link className="m-card-link" to={`/m/patients/${appt.patientId}`}>{appt.patientName}</Link>
              </div>
              <div className="m-doc-spec">{timeLabel(appt.scheduledAt)} · {appt.doctorName}</div>
            </div>
          </>
        ) : staffView ? (
          <>
            <Avatar name={appt.patientName} size="sm" />
            <div className="m-doc-body">
              <div className="m-doc-name">
                <Link className="m-card-link" to={`/m/patients/${appt.patientId}`}>{appt.patientName}</Link>
              </div>
              <div className="m-doc-spec">
                {ageLabel(appt)}/{genderShort(appt.gender)} · {appt.patientCode}
                {appt.category !== undefined && <> <CategoryPill category={appt.category} compact /></>}
              </div>
            </div>
          </>
        ) : (
          <>
            <Avatar name={appt.doctorName} size="sm" src={doctor?.portraitUrl} />
            <div className="m-doc-body">
              <div className="m-doc-name">
                {/* The name opens the doctor's profile; the rest of the card, where
                    the list makes it tappable, opens the booking itself. */}
                <Link className="m-card-link" to={`/m/doctors/${appt.doctorId}`}>{appt.doctorName}</Link>
              </div>
              <div className="m-doc-spec">{doctor?.specialization ?? t('shell.consultation')}</div>
            </div>
          </>
        )}
        {headEnd}
      </div>
      <div className="m-appt-rows">
        {desk ? (
          <div className="m-appt-row"><Icon name="user" size={16} />
            <span>{ageLabel(appt)}/{genderShort(appt.gender)} · {appt.patientCode}{appt.mobile ? ` · ${appt.mobile}` : ''}</span>
          </div>
        ) : (
          <>
            {/* Day and time side by side, each with its own icon. As one run
                of text - "Mon, 21 Sep at 09:30 AM IST" - the reader had to
                parse the whole string to find either half. */}
            <div className="m-appt-when">
              <span><Icon name="calendar" size={16} />{dayLabel(appt.scheduledAt)}</span>
              <span><Icon name="clock" size={16} />{timeLabel(appt.scheduledAt)}</span>
            </div>
            {line && (
              <div className="m-appt-row m-appt-queue"><Icon name="users" size={16} /><span>{line}</span></div>
            )}
          </>
        )}
        {!desk && staffView ? (
          <div className="m-appt-row"><Icon name="stethoscope" size={16} />
            <span>
              <Link className="m-card-link" to={`/m/doctors/${appt.doctorId}`}>{appt.doctorName}</Link>
              {doctor?.specialization ? ` · ${doctor.specialization}` : ''}
            </span>
          </div>
        ) : !desk && (
          <>
            <div className="m-appt-row"><Icon name="user" size={16} />
              <span>
                <strong>{appt.patientName}</strong> · {ageLabel(appt)}/{genderShort(appt.gender)} · {appt.patientCode}
                {appt.category !== undefined && <> <CategoryPill category={appt.category} compact /></>}
              </span>
            </div>
            <div className="m-appt-row"><Icon name="pin" size={16} /><span>{settings?.clinicName ?? t('shell.clinic')}</span></div>
          </>
        )}
        {/* What is owed and how it is meant to be paid. The card is the only
            place a patient sees this - the detail screen loads by doctor and
            date, which their role cannot query. */}
        {appt.paymentAmount != null && Number(appt.paymentAmount) > 0 && (
          <div className="m-appt-row">
            <Icon name="rupee" size={16} />
            <span>
              {money(appt.paymentAmount)}
              {/* "Card at clinic" is a plan. Once the money is in, the mode
                  is a fact and reads as one - it may well have been paid in
                  the app, nowhere near the clinic. */}
              {appt.paymentMode
                ? ` · ${appt.paymentStatus === 'settled'
                  ? t(`payMode.${appt.paymentMode}`)
                  : t(`bookingPayMode.${appt.paymentMode}`)}`
                : ''}
              {' '}
              <span className={`m-pill ${appt.paymentStatus === 'settled' ? 'ok' : ''}`}>
                {appt.paymentStatus === 'settled' ? t('common.paid') : t('common.due')}
              </span>
            </span>
            {/* A sibling of the text, not a child of it: inside the span it
                wrapped onto its own line and dragged the row's icon out of
                alignment with every other row on the card. */}
            {onPay && (
              <button
                type="button" className="m-btn sm" style={{ marginLeft: 'auto' }} disabled={busy}
                onClick={(e) => { e.stopPropagation(); onPay() }}
              >
                {t('shell.payNow')}
              </button>
            )}
          </div>
        )}
        {appt.allergies && (
          <div className="m-appt-row alert"><Icon name="alert" size={16} /><span>{t('shell.allergyLine', { allergies: appt.allergies })}</span></div>
        )}
        {missed && (
          <div className="m-appt-row m-appt-missed"><Icon name="clock" size={16} />
            <span>
              {onAction
                ? t('shell.missedDesk')
                : t('shell.missedPatient')}
            </span>
          </div>
        )}
      </div>
      {/* A missed booking cannot be confirmed or checked in - its day is
          over - so the desk gets only the ways to close it out. */}
      {onAction && missed && (
        <div className="m-appt-actions">
          <div className="secondary">
            <button type="button" className="m-btn sm light" disabled={busy} onClick={() => onAction('no-show')}>{t('shell.markNoShow')}</button>
            <button type="button" className="m-btn sm light" disabled={busy} onClick={() => onAction('reschedule')}>{t('move')}</button>
            <button type="button" className="m-btn sm danger" disabled={busy} onClick={() => onAction('cancel')}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
      {onAction && live && (
        /*
         * Two tiers, because the five actions are not five equal choices.
         *
         * Confirm and check-in are the path a booking actually travels, and
         * they get the prominent pair. Moving, marking a no-show and
         * cancelling are the exceptions, and stay small underneath - giving
         * all five the same weight turned the card into a wall of buttons
         * that buried the two anyone reaches for.
         */
        <div className="m-appt-actions">
          <div className="primary">
            {appt.status === 'booked' && (
              <button type="button" className="m-btn" disabled={busy} onClick={() => onAction('confirm')}>
                <Icon name="check" size={15} /> {t('common.confirm')}
              </button>
            )}
            {appt.status !== 'in_consult' && appt.status !== 'arrived' && (
              <button type="button" className="m-btn ghost" disabled={busy} onClick={() => onAction('checkin')}>{t('shell.checkIn')}</button>
            )}
          </div>
          <div className="secondary">
            {appt.status !== 'in_consult' && (
              <button type="button" className="m-btn sm light" disabled={busy} onClick={() => onAction('reschedule')}>{t('move')}</button>
            )}
            {appt.status === 'booked' && (
              <button type="button" className="m-btn sm light" disabled={busy} onClick={() => onAction('no-show')}>{t('apptStatus.no_show')}</button>
            )}
            <button type="button" className="m-btn sm danger" disabled={busy} onClick={() => onAction('cancel')}>{t('common.cancel')}</button>
          </div>
        </div>
      )}
      {footer}
    </div>
  )
}
/* One queue row. Screens differ only in where it goes, what the second line
   says and what sits at the end, so those are the props. */
/** Structural rather than QueueRow: the consulting room reads a lighter
 *  shape off a different endpoint. */
export interface QueueRowLike {
  id: string
  tokenNo: number
  status: string
  visitType: keyof typeof VISIT_TYPE_LABEL
  patientName: string
  gender: Gender
  ageYears: number | null
  ageMonths: number | null
  allergies: string | null
  /** Diabetic or Non-Diabetic, shown beside the age when the source row carries it. */
  category?: PatientCategory | null
  /** Only needed when the row falls back to opening the patient's record. */
  patientId?: string
  /** Only needed when the row falls back to the doctor · visit-type line. */
  doctorName?: string
}

export function QueueRowCard({
  row, to, onOpen, subtitle, trailing, showAge = true,
}: {
  row: QueueRowLike
  /** Where the row opens. Defaults to the patient's record. */
  to?: string
  /** Used instead of navigating, when the screen has its own opening to do. */
  onOpen?: () => void
  /** Replaces the doctor · visit-type line. An allergy still takes priority. */
  subtitle?: ReactNode
  /** What sits at the end. Defaults to the visit's status pill. */
  trailing?: ReactNode
  showAge?: boolean
}) {
  const navigate = useNavigate()
  const { t } = useT()
  const status = row.status as VisitStatus
  const tokenClass = status === 'completed' ? ' done' : status === 'in_consult' ? ' now' : ''
  return (
    <button
      type="button"
      className="m-row tap"
      onClick={() => {
        if (onOpen) return onOpen()
        const dest = to ?? (row.patientId ? `/m/patients/${row.patientId}` : null)
        if (dest) navigate(dest)
      }}
    >
      <span className={`m-token${tokenClass}`}>{tokenLabel(row.tokenNo)}</span>
      <div className="body">
        <div className="t">
          {row.patientName}
          {showAge && <span className="m-note"> {ageLabel(row)}/{genderShort(row.gender)}</span>}
          {row.category !== undefined && <> <CategoryPill category={row.category} compact /></>}
        </div>
        <div className={`s${row.allergies ? ' alert' : ''}`}>
          {row.allergies
            ? t('shell.allergyLine', { allergies: row.allergies })
            : subtitle ?? [row.doctorName, t(`visitType.${row.visitType}`)].filter(Boolean).join(' · ')}
        </div>
      </div>
      {trailing ?? <span className={`m-pill ${visitPillClass(status)}`}>{t(`visitStatus.${status}`)}</span>}
    </button>
  )
}

/* ---------- serial numbers, sharing, form errors ---------- */

/** A queue serial number as it is printed and called out: "#7". */
export function tokenLabel(n: number | string | null | undefined): string {
  return n == null || n === '' ? '#—' : `#${n}`
}

/**
 * A wa.me link that opens WhatsApp with the message typed in.
 *
 * Given a mobile it opens the chat with that number (a bare ten-digit Indian
 * number gets its 91 country code); without one WhatsApp asks who to send it
 * to, which is what a patient forwarding their own ticket wants.
 */
export function whatsappLink(text: string, mobile?: string | null): string {
  let digits = (mobile ?? '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1)
  if (digits.length === 10) digits = `91${digits}`
  const base = digits ? `https://wa.me/${digits}` : 'https://wa.me/'
  return `${base}?text=${encodeURIComponent(text)}`
}

export function ShareWhatsApp({ text, mobile, label: given, block, sm, iconOnly }: {
  text: string
  mobile?: string | null
  label?: string
  block?: boolean
  sm?: boolean
  /** A round icon button, for tight spots such as a card's heading row. */
  iconOnly?: boolean
}) {
  const { t } = useT()
  const label = given ?? t('shell.shareOnWhatsApp')
  if (iconOnly) {
    return (
      <a
        className="m-wa-icon" href={whatsappLink(text, mobile)} target="_blank" rel="noopener noreferrer"
        aria-label={label} title={label}
        // Inside a tappable card: the share must not also open the booking.
        onClick={(e) => e.stopPropagation()}
      >
        <Icon name="whatsapp" size={18} />
      </a>
    )
  }
  return (
    <a
      className={`m-btn whatsapp${block ? ' block' : ''}${sm ? ' sm' : ''}`}
      href={whatsappLink(text, mobile)} target="_blank" rel="noopener noreferrer"
    >
      <Icon name="whatsapp" size={sm ? 15 : 18} /> {label}
    </a>
  )
}

/** Digits for an SMS link: a 10-digit Indian mobile gets its +91. */
function smsNumber(mobile?: string | null): string {
  let d = (mobile ?? '').replace(/\D/g, '')
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1)
  return d.length === 10 ? `+91${d}` : d ? `+${d}` : ''
}

/**
 * Puts text on the clipboard. The clinic LAN serves plain http, where the
 * async clipboard API is not allowed, so the old execCommand path backs it up.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'; ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}

/**
 * Share a message through whatever app the person wants.
 *
 * Where the device has a share sheet (Chrome on Android, Safari on iOS) that
 * sheet opens, and every installed app is on it. Where it does not - the
 * Android app's WebView, desktop browsers - an in-app sheet offers WhatsApp,
 * a text message and email addressed to the patient, and a plain copy for
 * anything else.
 */
export function ShareButton({ text, title, mobile, label, className = 'm-btn' }: {
  text: string
  /** Subject line for email, and the share sheet's title. */
  title: string
  /** The patient's number, so WhatsApp and SMS open addressed to them. */
  mobile?: string | null
  label?: string
  className?: string
}) {
  const { toast } = useMobile()
  const { t } = useT()
  const [open, setOpen] = useState(false)

  async function share() {
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void>; canShare?: (d: ShareData) => boolean }
    const data: ShareData = { title, text }
    if (nav.share && (!nav.canShare || nav.canShare(data))) {
      try {
        await nav.share(data)
        return
      } catch (err) {
        // Closing the sheet is a choice, not a failure.
        if ((err as { name?: string })?.name === 'AbortError') return
      }
    }
    setOpen(true)
  }

  async function copy() {
    const ok = await copyText(text)
    toast(ok
      ? { kind: 'ok', text: t('shell.copied') }
      : { kind: 'error', text: t('shell.copyFailed') })
    if (ok) setOpen(false)
  }

  const sms = `sms:${smsNumber(mobile)}?body=${encodeURIComponent(text)}`
  const mail = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`

  return (
    <>
      <button type="button" className={className} onClick={() => void share()}>
        <Icon name="share" size={18} /> {label ?? t('common.share')}
      </button>
      {open && (
        <div className="m-sheet-backdrop" onClick={() => setOpen(false)}>
          <div
            className="m-sheet m-share-sheet" role="dialog" aria-modal="true" aria-labelledby="share-sheet-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grab" />
            <h3 id="share-sheet-title">{t('shell.shareTitle', { title: title.toLowerCase() })}</h3>
            <div className="m-share-options">
              <a className="m-btn whatsapp" href={whatsappLink(text, mobile)} target="_blank" rel="noopener noreferrer"
                onClick={() => setOpen(false)}>
                <Icon name="whatsapp" size={18} /> WhatsApp
              </a>
              <a className="m-btn light" href={sms} onClick={() => setOpen(false)}>
                <Icon name="message" size={18} /> {t('shell.textMessage')}
              </a>
              <a className="m-btn light" href={mail} onClick={() => setOpen(false)}>
                <Icon name="mail" size={18} /> {t('common.email')}
              </a>
              <button type="button" className="m-btn light" onClick={() => void copy()}>
                <Icon name="clipboard" size={18} /> {t('shell.copyText')}
              </button>
            </div>
            <div className="m-sheet-actions">
              <button type="button" className="m-btn light" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/** The complaint under a field, announced to screen readers as it appears. */
export function FieldError({ children }: { children: string | null | undefined }) {
  if (!children) return null
  return <span className="m-field-err" role="alert">{children}</span>
}

/**
 * Today's queue in the order patients are called: by serial number, and
 * between two doctors' patients holding the same number, by doctor.
 */
export function sortQueue<T extends { tokenNo: number; doctorName?: string | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.tokenNo - b.tokenNo || (a.doctorName ?? '').localeCompare(b.doctorName ?? ''))
}

/**
 * Today's queue as one section per doctor, each in serial-number order,
 * doctors in roster order.
 *
 * A serial number is unique per doctor per day (the database holds it to
 * that: visits_token_uq), so every doctor's line starts at #1. Listed as one
 * run, four doctors' #1s sat side by side and nobody could tell whose was
 * whose; sectioned, each number means exactly one patient.
 */
export function groupQueueByDoctor<T extends { doctorId: string; doctorName?: string | null; tokenNo: number; status: string }>(
  rows: T[],
  doctors: { id: string }[],
): { doctorId: string; doctorName: string; rows: T[]; nowServing: number | null; waiting: number }[] {
  return groupByDoctor(rows, doctors).map((g) => {
    const sorted = sortQueue(g.rows)
    return {
      ...g,
      rows: sorted,
      nowServing: sorted.find((v) => v.status === 'in_consult')?.tokenNo ?? null,
      waiting: sorted.filter((v) => v.status === 'waiting').length,
    }
  })
}

/**
 * Any day list split into one section per doctor, doctors in roster order,
 * each section keeping the rows in the order they came. Serial numbers -
 * visit tokens and booking serials alike - run per doctor per day, so a list
 * spanning doctors reads correctly only when it is sectioned this way.
 */
export function groupByDoctor<T extends { doctorId: string; doctorName?: string | null }>(
  rows: T[],
  doctors: { id: string }[],
): { doctorId: string; doctorName: string; rows: T[] }[] {
  const order = new Map(doctors.map((d, i) => [d.id, i]))
  const byDoctor = new Map<string, T[]>()
  for (const v of rows) byDoctor.set(v.doctorId, [...(byDoctor.get(v.doctorId) ?? []), v])
  return [...byDoctor.entries()]
    .sort(([a], [b]) => (order.get(a) ?? 99) - (order.get(b) ?? 99))
    .map(([doctorId, list]) => ({ doctorId, doctorName: list[0]?.doctorName ?? tr('common.doctor'), rows: list }))
}

/**
 * A short buzz to confirm something landed - a booking made, a patient
 * checked in. Phones that cannot vibrate, and desktops, simply skip it.
 */
export function haptic(pattern: number | number[] = 30): void {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(pattern)
  } catch { /* some webviews throw rather than ignore */ }
}

/** One change to a doctor's hours, as `/api/schedule/changes` reports it. */
export interface ScheduleChange {
  id: string
  at: string
  what: string
  byName: string
  byRole: string | null
  onBehalf: boolean
}

export async function loadScheduleChanges(doctorId: string, limit = 10): Promise<ScheduleChange[]> {
  const r = await api.get<{ changes: ScheduleChange[] }>(`/api/schedule/changes?doctorId=${doctorId}&limit=${limit}`)
  return r.changes
}

/** When the doctor last looked at changes made to their hours, per device. */
export function scheduleSeenKey(doctorId: string): string {
  return `clinic.m.scheduleSeen.${doctorId}`
}

/**
 * A piece of screen state kept in the address, so a filter survives the back
 * button, a reload and a link sent to a colleague. Other parameters already
 * in the address are left alone, and the default is kept out of it.
 */
export function useParamState<T extends string>(key: string, fallback: T): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams()
  const value = (params.get(key) as T | null) ?? fallback
  const set = useCallback((next: T) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev)
      if (next === fallback || next === '') p.delete(key)
      else p.set(key, next)
      return p
    }, { replace: true })
  }, [key, fallback, setParams])
  return [value, set]
}

/** An action revealed by swiping a row left. */
export interface SwipeAction {
  label: string
  icon?: IconName
  tone?: 'ok' | 'danger' | 'primary'
  onClick: () => void
}

/**
 * A row that slides left to show a few quick actions, the way a phone's mail
 * list does. A short drag springs back; past the threshold it stays open
 * until tapped elsewhere. Mouse and keyboard users still have every action
 * on the screen the row opens, so this is a shortcut, never the only way.
 */
export function SwipeRow({ actions, children }: { actions: SwipeAction[]; children: ReactNode }) {
  const width = actions.length * 76
  const [dx, setDx] = useState(0)
  const [open, setOpen] = useState(false)
  const start = useRef<{ x: number; y: number; base: number; horizontal: boolean | null } | null>(null)

  if (actions.length === 0) return <>{children}</>

  const offset = open && !start.current ? -width : dx
  return (
    <div className="m-swipe" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) { setOpen(false); setDx(0) } }}>
      <div className="m-swipe-actions" style={{ width }} aria-hidden={!open}>
        {actions.map((a) => (
          <button
            key={a.label} type="button" tabIndex={open ? 0 : -1}
            className={`m-swipe-btn ${a.tone ?? 'primary'}`}
            onClick={() => { setOpen(false); setDx(0); haptic(); a.onClick() }}
          >
            {a.icon && <Icon name={a.icon} size={18} />}
            <span>{a.label}</span>
          </button>
        ))}
      </div>
      <div
        className="m-swipe-front"
        style={{ transform: `translateX(${offset}px)`, transition: start.current ? 'none' : 'transform .2s ease' }}
        onTouchStart={(e) => {
          const t = e.touches[0]!
          start.current = { x: t.clientX, y: t.clientY, base: open ? -width : 0, horizontal: null }
          setDx(open ? -width : 0)
        }}
        onTouchMove={(e) => {
          const s = start.current
          if (!s) return
          const t = e.touches[0]!
          const mx = t.clientX - s.x
          const my = t.clientY - s.y
          // Decide once whether this is a swipe or a scroll, then stick to it.
          if (s.horizontal === null && (Math.abs(mx) > 8 || Math.abs(my) > 8)) s.horizontal = Math.abs(mx) > Math.abs(my)
          if (!s.horizontal) return
          setDx(Math.max(-width - 24, Math.min(0, s.base + mx)))
        }}
        onTouchEnd={() => {
          const s = start.current
          start.current = null
          if (!s?.horizontal) { setDx(open ? -width : 0); return }
          const shouldOpen = dx < -width / 2
          setOpen(shouldOpen)
          setDx(shouldOpen ? -width : 0)
          if (shouldOpen && !open) haptic(15)
        }}
        onClickCapture={(e) => {
          // A tap on an open row closes it rather than opening the record.
          if (open) { e.stopPropagation(); e.preventDefault(); setOpen(false); setDx(0) }
        }}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * The specialty picker: All plus each specialty, as icon tiles.
 *
 * With eleven specialties a full grid took most of a phone screen, so it
 * shows two rows - All, the first few, and a More tile - and opens the rest
 * on request. The selected specialty is always among the visible tiles, so
 * collapsing never hides what is currently filtering the list.
 */
export function SpecialtyGrid({ specialties, value, onChange, collapsedCount = 4 }: {
  specialties: string[]
  value: string | null
  onChange: (next: string | null) => void
  /** Specialty tiles shown while collapsed, besides All and More. */
  collapsedCount?: number
}) {
  const [open, setOpen] = useState(false)
  const { t } = useT()
  const needsMore = specialties.length > collapsedCount + 1
  let visible = specialties
  if (needsMore && !open) {
    visible = specialties.slice(0, collapsedCount)
    if (value && !visible.includes(value)) visible = [...visible.slice(0, collapsedCount - 1), value]
  }
  const hidden = specialties.length - visible.length
  return (
    <div className="m-spec-grid">
      <button
        type="button" className={`m-spec${value === null ? ' is-on' : ''}`}
        aria-pressed={value === null} onClick={() => onChange(null)}
      >
        <span className="ico"><Icon name="grid" size={24} /></span><span>{t('common.all')}</span>
      </button>
      {visible.map((s) => (
        <button
          key={s} type="button" className={`m-spec${value === s ? ' is-on' : ''}`}
          aria-pressed={value === s}
          onClick={() => onChange(value === s ? null : s)}
        >
          <span className="ico"><Icon name={specialtyIcon(s)} size={24} /></span><span>{s}</span>
        </button>
      ))}
      {needsMore && (
        <button type="button" className="m-spec more" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <span className="ico"><Icon name={open ? 'x' : 'plus'} size={22} /></span>
          <span>{open ? t('common.less') : t('shell.nMore', { n: hidden })}</span>
        </button>
      )}
    </div>
  )
}

/**
 * Patient search with its matches in a dropdown under the box, rather than a
 * wall of chips pushing the rest of the form off the screen. Each row carries
 * age/gender, code and mobile, because families share a number and picking
 * the wrong member is the expensive mistake. Arrow keys and Enter work too.
 */
export function PatientSearchDropdown({ query, onQuery, matches, onPick, error, autoFocus, placeholder }: {
  query: string
  onQuery: (q: string) => void
  matches: Array<{
    id: string; fullName: string; patientCode: string; mobile: string | null
    gender: Gender; ageYears: number | null; ageMonths: number | null
  }>
  onPick: (id: string) => void
  error?: string | null
  autoFocus?: boolean
  placeholder?: string
}) {
  const [focused, setFocused] = useState(false)
  const [active, setActive] = useState(0)
  const { t } = useT()
  const typed = query.trim().length >= 2
  const open = focused && typed
  useEffect(() => { setActive(0) }, [matches])

  return (
    <div className="m-dd">
      <div className="m-search">
        <Icon name="search" size={18} />
        <input
          role="combobox" aria-expanded={open} aria-controls="m-dd-list" aria-autocomplete="list"
          aria-label={t('shell.searchPatients')} autoComplete="off" autoFocus={autoFocus}
          placeholder={placeholder ?? t('shell.patientSearchPh')} value={query}
          onChange={(e) => onQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          // Delay so a tap on a row lands before the list closes.
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => {
            if (!open || matches.length === 0) return
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, matches.length - 1)) }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)) }
            if (e.key === 'Enter') { e.preventDefault(); onPick(matches[active]!.id) }
            if (e.key === 'Escape') setFocused(false)
          }}
        />
        {query && <button type="button" aria-label={t('shell.clear')} onClick={() => onQuery('')}><Icon name="x" size={16} /></button>}
      </div>
      {open && (
        <div className="m-dd-list" id="m-dd-list" role="listbox">
          {error ? (
            <div className="m-dd-empty alert" role="alert">{error}</div>
          ) : matches.length === 0 ? (
            <div className="m-dd-empty">{t('shell.noPatientMatch', { q: query.trim() })}</div>
          ) : matches.map((p, i) => (
            <button
              key={p.id} type="button" role="option" aria-selected={i === active}
              className={`m-dd-row${i === active ? ' is-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => { onPick(p.id); setFocused(false) }}
            >
              <Avatar name={p.fullName} size="sm" />
              <span className="body">
                <span className="t">{p.fullName} <span className="m-note">{ageLabel(p)}/{genderShort(p.gender)}</span></span>
                <span className="s">{[p.patientCode, p.mobile].filter(Boolean).join(' · ')}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * A doctor carousel with a heading: one full-width doctor per slide, a slow
 * auto-advance that pauses while the user is touching or pointing at it, dots
 * underneath, and previous/next arrows beside the heading. Built on
 * AutoSlider so every strip in the app pauses, loops and respects reduced
 * motion the same way. Used on the home screen and the bookings screen.
 *
 * The heading lives here so the arrows can sit beside it: floating over a
 * full-width card, an arrow covered the doctor's name.
 */
export function DoctorRail({ title, action, children }: {
  title: string
  /** Whatever the section already offered beside its heading, e.g. "See All". */
  action?: ReactNode
  children: ReactNode
}) {
  const { t } = useT()
  return (
    <AutoSlider
      full
      label={title}
      intervalMs={3800}
      head={({ prev, next }) => (
        <div className="m-section-head">
          <h2>{title}</h2>
          <div className="m-carousel-controls">
            {action}
            <button type="button" className="m-carousel-arrow" aria-label={t('shell.prevDoctor')} onClick={prev}>
              <Icon name="back" size={16} />
            </button>
            <button type="button" className="m-carousel-arrow" aria-label={t('shell.nextDoctor')} onClick={next}>
              <Icon name="chevron" size={16} />
            </button>
          </div>
        </div>
      )}
    >
      {children}
    </AutoSlider>
  )
}
